import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase/supabaseClient";
import Button from "../components/atoms/Button/Button";
import { AuthError } from "@supabase/supabase-js";
import { errorMessageTranslator, ErrorCode } from "./authHelpers";
import config from "../config";
import { useTranslation } from "react-i18next";
import i18n from "../i18n/i18n";

const ForgotPasswordForm = () => {
  const { t } = useTranslation();

  useEffect(() => {
    if (i18n.language !== navigator.language) {
      i18n.changeLanguage(navigator.language);
    }
  }, [t]);

  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleForgotPassword = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${config.api.BASE_URL}/update-password`,
      });
      if (error) throw error;
      setSuccess(true);
    } catch (error: unknown) {
      if (error instanceof AuthError) {
        const errorMessage = errorMessageTranslator(
          error.code as ErrorCode,
          "forgot-password"
        );
        setError(errorMessage);
      } else {
        setError(t("errorWhileRecoveringPassword"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form className="dui-auth-page" onSubmit={handleForgotPassword}>
      <div className="dui-auth-logo">
        <b>Doktor</b>UI
      </div>
      <div>
        {success ? (
          <div className="dui-auth-form">
            <div className="dui-auth-confirmation">
              <div className="dui-auth-title">{t("recoverPasswordTitle")}</div>
              <div className="dui-auth-confirmation-description">
                {t("recoverConfirmation")}
              </div>
            </div>
            <div className="dui-auth-form-footer">
              <a href="/login">{t("backToLogin")}</a>
            </div>
          </div>
        ) : (
          <div className="dui-auth-form">
            <div className="dui-auth-title">{t("recoverPasswordTitle")}</div>
            {error && (
              <div className="dui-auth-error">
                <div className="dui-auth-error-text">{error}</div>
              </div>
            )}
            <div className="dui-auth-form-fields-wrapper">
              <div className="dui-auth-form-fields">
                <div className="dui-auth-field">
                  <div className="dui-auth-field-label">{t("email")}</div>
                  <input
                    className="dui-auth-field-input"
                    type="email"
                    placeholder={t("yourEmail")}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <Button
                  className="dui-auth-button"
                  disabled={isLoading}
                  onClick={handleForgotPassword}
                  htmlType="submit"
                >
                  {isLoading
                    ? t("recoverConfirmInProgress")
                    : t("recoverConfirm")}
                </Button>
              </div>
              <div className="dui-auth-form-footer">
                {t("alreadyHaveAccount")} <a href="/login">{t("loginHere")}</a>
              </div>
            </div>
          </div>
        )}
      </div>
    </form>
  );
};

export default ForgotPasswordForm;
